import { computed, ref } from 'vue'
import { useAuth } from '@/composables/auth'
import { apiPost } from '@/composables/useApi'
import { ONBOARDING_STEPS, hasProfile as profileHasContent, normalizeProfile, shouldShowOnboarding } from '@/utils/learnerProfile'
import { startProfileSync } from '@/utils/profileSync'

const PROFILE_KEY = 'gs.learnerProfile'
const DISMISSED_KEY = 'gs.profileOnboardingDismissed'
const SETUP_KEY = 'gs.profileSetup'

function readStorage(key) {
  try {
    return window.localStorage.getItem(key)
  } catch {
    return null
  }
}

function writeStorage(key, value) {
  try {
    window.localStorage.setItem(key, value)
  } catch {
    // 隐私模式等情况下无法写入，档案只在本次页面里有效
  }
}

function readLocalProfile() {
  try {
    return normalizeProfile(JSON.parse(readStorage(PROFILE_KEY) || 'null'))
  } catch {
    return normalizeProfile(null)
  }
}

// 模块级单例：所有页面共用同一份本地档案和引导状态
const localProfile = ref(readLocalProfile())
const onboardingDismissals = ref({})
const onboardingSessions = ref({})
let syncStarted = false

function readSetupSession(key) {
  try {
    const session = JSON.parse(window.sessionStorage.getItem(key) || 'null')
    if (!session || !Number.isInteger(session.step) || session.step < 0 || session.step >= ONBOARDING_STEPS.length) return null
    return {
      draft: normalizeProfile(session.draft),
      step: session.step,
      testingSkill: ['listening', 'speaking'].includes(session.testingSkill) ? session.testingSkill : null
    }
  } catch {
    return null
  }
}

function getSetupSession(key) {
  return key in onboardingSessions.value ? onboardingSessions.value[key] : readSetupSession(key)
}

function storeSetupSession(key, session) {
  onboardingSessions.value = { ...onboardingSessions.value, [key]: session }
  try {
    if (session) window.sessionStorage.setItem(key, JSON.stringify(session))
    else window.sessionStorage.removeItem(key)
  } catch {
    // 无法使用会话存储时，页面内跳转仍保留进度。
  }
}

export function useLearnerProfile() {
  const { user, userData, userDataReady, authResolved } = useAuth()
  const setupKey = computed(() => `${SETUP_KEY}:${user.value?.uid || 'guest'}`)
  const onboardingSession = computed(() => getSetupSession(setupKey.value))
  const saveOnboardingProgress = (draft, step, testingSkill = null) => {
    storeSetupSession(setupKey.value, { draft: normalizeProfile(draft), step, testingSkill })
  }
  const adoptGuestOnboarding = () => {
    if (!user.value || onboardingSession.value) return
    const guestKey = `${SETUP_KEY}:guest`
    const session = getSetupSession(guestKey)
    if (!session) return
    storeSetupSession(setupKey.value, session)
    storeSetupSession(guestKey, null)
  }
  const completeOnboardingTest = (skill, level) => {
    const session = onboardingSession.value
    if (session?.testingSkill !== skill) return
    const field = `${skill}Level`
    if (!normalizeProfile({ [field]: level })[field]) return
    // 测完回到发起测试的那一步，引导里显示结果
    saveOnboardingProgress({ ...session.draft, [`${skill}Level`]: level, [`${skill}Source`]: 'test' }, session.step)
  }
  const dismissalKey = computed(() => user.value ? `${DISMISSED_KEY}:${user.value.uid}` : DISMISSED_KEY)
  const onboardingDismissed = computed(() => (
    onboardingDismissals.value[dismissalKey.value] ?? (readStorage(dismissalKey.value) === '1')
  ))

  const profile = computed(() => (
    user.value ? normalizeProfile(userData.value?.learnerProfile) : localProfile.value
  ))
  const hasProfile = computed(() => profileHasContent(profile.value))
  // 登录状态和账号档案都到了，profile 才是最终值
  const profileReady = computed(() => authResolved.value && (!user.value || userDataReady.value))
  const showOnboarding = computed(() => shouldShowOnboarding({
    authResolved: authResolved.value,
    loggedIn: Boolean(user.value),
    userDataReady: userDataReady.value,
    hasProfile: hasProfile.value && !onboardingSession.value,
    dismissed: onboardingDismissed.value && !onboardingSession.value
  }))

  const saveProfile = async next => {
    const normalized = normalizeProfile(next)
    if (user.value) {
      // 账号档案以后端为准，Firestore 监听会把新值推回 userData；不写本地，免得换账号时串到别人身上
      await apiPost('/api/users/learner-profile', normalized)
      return
    }
    localProfile.value = normalized
    writeStorage(PROFILE_KEY, JSON.stringify(normalized))
  }

  const dismissOnboarding = () => {
    storeSetupSession(setupKey.value, null)
    onboardingDismissals.value = { ...onboardingDismissals.value, [dismissalKey.value]: true }
    writeStorage(dismissalKey.value, '1')
  }

  if (!syncStarted) {
    syncStarted = true
    startProfileSync({
      ready: computed(() => Boolean(user.value) && userDataReady.value),
      getLocal: () => localProfile.value,
      getRemote: () => userData.value?.learnerProfile,
      upload: profileToSync => apiPost('/api/users/learner-profile', profileToSync).catch(() => {})
    })
  }

  return { profile, hasProfile, profileReady, showOnboarding, onboardingDismissed, saveProfile, dismissOnboarding, onboardingSession, saveOnboardingProgress, completeOnboardingTest, adoptGuestOnboarding }
}
